// Searchable language selector modal (keyboard accessible, touch-friendly).
import {
  I18n,
  localeNativeName,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@aerofruits/i18n';

const REGION_ORDER: { groupKey: string; locales: SupportedLocale[] }[] = [
  {
    groupKey: 'common.language.group.europe',
    locales: [
      'en', 'ru', 'lv', 'de', 'fr', 'es', 'it', 'pt', 'pl', 'uk', 'ro', 'nl', 'cs', 'sk', 'hu', 'bg',
      'el', 'sv', 'nb', 'da', 'fi', 'lt', 'et', 'hr', 'sr', 'sl', 'tr',
    ],
  },
  {
    groupKey: 'common.language.group.asia',
    locales: ['zh-CN', 'zh-TW', 'ja', 'ko', 'hi', 'id', 'vi', 'th', 'ms', 'fil'],
  },
  {
    groupKey: 'common.language.group.middleEast',
    locales: ['ar', 'he', 'fa'],
  },
  {
    groupKey: 'common.language.group.americas',
    locales: ['pt-BR', 'es-419'],
  },
];

export interface LanguageSelectorOptions {
  i18n: I18n;
  root: HTMLElement;
  onSelect: (locale: SupportedLocale) => void | Promise<void>;
}

function lockPageScroll(): void {
  document.body.classList.add('modal-open');
}

function unlockPageScroll(): void {
  document.body.classList.remove('modal-open');
}

function applyDrawerViewport(root: HTMLElement): void {
  const vv = window.visualViewport;
  if (!vv) return;
  const topInset = Math.max(0, vv.offsetTop);
  const bottomInset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
  root.style.maxHeight = `calc(100dvh - ${topInset + bottomInset}px - env(safe-area-inset-top) - env(safe-area-inset-bottom))`;
  root.style.top = `${topInset}px`;
}

export function mountLanguageSelector(opts: LanguageSelectorOptions): {
  open: () => void;
  close: () => void;
  refresh: () => void;
} {
  const { root, i18n, onSelect } = opts;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', i18n.t('common.language.title'));

  const head = document.createElement('div');
  head.className = 'drawer__head';
  const title = document.createElement('h2');
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'iconbtn';
  closeBtn.id = 'language-close';

  const searchWrap = document.createElement('div');
  searchWrap.className = 'language-search';
  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'input language-search__input';
  search.id = 'language-search';
  search.autocomplete = 'off';
  search.setAttribute('enterkeyhint', 'search');
  searchWrap.appendChild(search);

  const list = document.createElement('div');
  list.className = 'language-list';
  list.id = 'language-list';

  root.appendChild(head);
  head.appendChild(title);
  head.appendChild(closeBtn);
  root.appendChild(searchWrap);
  root.appendChild(list);

  let lastFocus: HTMLElement | null = null;
  let viewportHandler: (() => void) | null = null;

  const refresh = (): void => {
    title.textContent = i18n.t('common.language.title');
    closeBtn.textContent = i18n.t('common.actions.close');
    closeBtn.setAttribute('aria-label', i18n.t('common.actions.close'));
    search.placeholder = i18n.t('common.language.search');
    search.setAttribute('aria-label', i18n.t('common.language.search'));
    renderList(search.value);
  };

  const renderList = (filter: string): void => {
    list.replaceChildren();
    const q = filter.trim().toLowerCase();
    const current = i18n.getLocale();
    for (const group of REGION_ORDER) {
      const items = group.locales.filter((loc) => {
        if (!SUPPORTED_LOCALES.includes(loc)) return false;
        if (!q) return true;
        const native = localeNativeName(loc).toLowerCase();
        const english = loc.toLowerCase();
        return native.includes(q) || english.includes(q) || loc.toLowerCase().includes(q);
      });
      if (items.length === 0) continue;
      const heading = document.createElement('h3');
      heading.className = 'language-list__group';
      heading.textContent = i18n.t(group.groupKey);
      list.appendChild(heading);
      for (const loc of items) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'language-list__item' + (loc === current ? ' language-list__item--active' : '');
        btn.dataset.locale = loc;
        btn.textContent = localeNativeName(loc);
        if (loc === current) btn.setAttribute('aria-current', 'true');
        btn.addEventListener('click', () => {
          void Promise.resolve(onSelect(loc)).then(() => close());
        });
        list.appendChild(btn);
      }
    }
  };

  const open = (): void => {
    lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    refresh();
    lockPageScroll();
    applyDrawerViewport(root);
    viewportHandler = () => applyDrawerViewport(root);
    window.visualViewport?.addEventListener('resize', viewportHandler);
    window.visualViewport?.addEventListener('scroll', viewportHandler);
    root.removeAttribute('hidden');
    search.focus({ preventScroll: true });
  };

  const close = (): void => {
    root.setAttribute('hidden', '');
    search.value = '';
    if (viewportHandler) {
      window.visualViewport?.removeEventListener('resize', viewportHandler);
      window.visualViewport?.removeEventListener('scroll', viewportHandler);
      viewportHandler = null;
    }
    root.style.maxHeight = '';
    root.style.top = '';
    unlockPageScroll();
    lastFocus?.focus();
  };

  closeBtn.addEventListener('click', close);
  search.addEventListener('input', () => renderList(search.value));
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  });

  return { open, close, refresh };
}
